import {RequestMethod} from '#lit/common/constants/index.js';
import {FlyErrorWarning} from '#lit/utils/errors.js';
import {fetcher} from '#lit/utils/http.js';
import type {
  AudienceExport,
  AudienceExportActivitiesResponse,
  ApiError,
  ExportErrorLogsResponse,
} from '#lit/types/audience_exports.js';
import {isPlainObject} from '../utils/index.js';
import type {PrimaryTableField} from '#lit/types/datasets.js';

type AudienceExportActivitiesErrorResponse = {
  error_message?: string;
  errors?: ApiError[];
};

function isResponseError(
  err: unknown
): err is AudienceExportActivitiesErrorResponse {
  if (!isPlainObject(err)) return false;
  return 'error_message' in err || 'errors' in err;
}

function parseResponseError(err: AudienceExportActivitiesErrorResponse) {
  if (err.error_message) return err.error_message;
  if (err.errors) return err.errors.map(e => e.title).join('; ');
}

const BASE_URL = (exportId: number) =>
  `/audiences/audiences/audience_export/${exportId}`;

export type FieldSearchResponse = {
  message: string;
  more: boolean;
  results: PrimaryTableField[];
  page: number;
};

class ExportService {
  /**
   * Update a dataset
   */

  async getAudienceExportActivities(
    audienceId: string
  ): Promise<AudienceExportActivitiesResponse> {
    if (!audienceId) {
      throw new Error(
        'Unable to retrieve audience export activities with that audience id'
      );
    }

    const url = new URL(
      `/api/export_activities?type=audience&id=${audienceId}`,
      location.origin
    );

    try {
      const response = await fetcher<AudienceExportActivitiesResponse>(
        url.toString(),
        {
          method: RequestMethod.GET,
        }
      );
      if (response.error) throw new Error(response.error);
      return response;
    } catch (err) {
      console.error(err);
      throw new Error('unable to get export activity');
    }
  }

  async getPrimaryTableFields(
    datasetGroupId: number,
    datasetId: number,
    journeyId: string | null,
    limit: number | null
  ): Promise<FieldSearchResponse> {
    try {
      const url = new URL(
        `/api/v2/dataset_groups/${datasetGroupId}/field_search`,
        location.origin
      );

      url.searchParams.append('dataset_id', datasetId.toString());
      url.searchParams.append('fields_type', 'journey');
      if (journeyId) url.searchParams.append('journey_id', journeyId);
      if (limit) url.searchParams.append('limit', limit.toString());
      url.searchParams.append('include_pii_if_allowed', 'true');
      url.searchParams.append('include_system_and_computed_fields', 'false');
      url.searchParams.append('exclude_rejected_custom_fields', 'true');

      const response = await fetcher<FieldSearchResponse>(url.toString(), {
        method: RequestMethod.GET,
      });

      return response;
    } catch (err: unknown) {
      console.error(err);
      throw new Error('Unable to fetch primary table fields');
    }
  }

  async getAudienceExportRunLogs(
    audienceID: string,
    export_run_id: string,
    error_file = ''
  ): Promise<ExportErrorLogsResponse> {
    if (!audienceID || !export_run_id) {
      throw new Error(
        'Unable to retrieve audience export activities with that id'
      );
    }
    const qs = new URLSearchParams({
      export_run_id,
      type: 'audience',
      id: audienceID,
      error_file,
    });
    const url = new URL(`/api/export_activities/errors?${qs}`, location.origin);
    try {
      const response = await fetcher<ExportErrorLogsResponse>(url.toString(), {
        method: RequestMethod.GET,
      });
      return response;
    } catch (err: unknown) {
      console.error(err);

      let errorMessage = '';

      if (isResponseError(err)) {
        errorMessage =
          err.error_message ||
          err.errors!.map((e: ApiError) => e.title).join('; ');
      }

      throw Error(errorMessage || 'Unable to fetch export run logs.');
    }
  }

  async getExportRunLogsByUrl(
    url: URL,
    export_run_id: string,
    error_file = ''
  ): Promise<ExportErrorLogsResponse> {
    if (!url || !export_run_id) {
      throw new Error(`Unable to retrieve logs from ${url}`);
    }

    url.searchParams.set('export_run_id', export_run_id);
    url.searchParams.set('error_file', error_file);

    try {
      return await fetcher<ExportErrorLogsResponse>(url.toString(), {
        method: RequestMethod.GET,
      });
    } catch (err: unknown) {
      console.error(err);

      throw new Error(
        isResponseError(err)
          ? parseResponseError(err)
          : 'Unable to fetch export run logs.'
      );
    }
  }

  async update(
    params: AudienceExport,
    formToken: string
  ): Promise<AudienceExport> {
    const url = BASE_URL(params.id as number);

    const payload = {
      audience_export: {
        field_mappings: {
          campaign_type: params.campaign_type,
        },
        sync_frequency: params.sync_frequency,
        ends_at: params.ends_at,
        schedule: params.schedule,
      },
    };

    try {
      return await fetcher<AudienceExport>(url, {
        method: RequestMethod.PATCH,
        body: payload,
        responseParser: 'json',
        headers: {
          'X-CSRF-Token': formToken,
        },
      });
    } catch (err) {
      new FlyErrorWarning(
        `Error updating: ${params.id}`,
        {
          label: 'request',
          data: `GET: ${url}`,
        },
        {
          label: 'response',
          data: err,
        }
      ).register('Error in export service');

      throw err;
    }
  }

  buildExportLogsUrl(
    params?: Record<string, string | number | boolean>,
    baseUrl = location.origin
  ): URL {
    const url = new URL('/api/export_activities/errors', baseUrl);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.set(key, value.toString());
      });
    }

    return url;
  }
}

export const exportService = new ExportService();
